import { FormEvent, useState } from "react";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import BankFactsForm from "./BankFactsForm";
import { factsBody, factsErrors, factsOf } from "../../utils/bankCaseUtils";
import { apiErrorMessage } from "../../utils/apiError";
import { BankCase, BankFactsBody, BankFactsValues } from "../../types/evidenceBankCase";

export default function EditBankCaseDialog({ bankCase, onClose, onSave }: {
  bankCase: BankCase;
  onClose: () => void;
  /** Rejects with the API's reason when the edit is refused. */
  onSave: (body: BankFactsBody) => Promise<void>;
}) {
  const [initial] = useState<BankFactsValues>(() => factsOf(bankCase));
  const [values, setValues] = useState<BankFactsValues>(initial);
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const errors = factsErrors(values);
  // A save of nothing would still mark the PDF as behind.
  const changed = JSON.stringify(values) !== JSON.stringify(initial);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    if (Object.keys(errors).length > 0) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(factsBody(values));
      onClose();
    } catch (err: unknown) {
      setError(apiErrorMessage(err, "Failed to save the dispute details"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={(_, reason) => { if (reason !== "backdropClick" && !saving) onClose(); }}
      maxWidth="sm"
      fullWidth
      aria-labelledby="edit-bank-case-title"
    >
      <form onSubmit={submit} noValidate>
        <DialogTitle id="edit-bank-case-title">Dispute details</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <BankFactsForm values={values} errors={tried ? errors : {}} disabled={saving} onChange={setValues} />
            <Typography variant="caption" color="text.secondary">
              The dispute ID and the merchant ID show in the preview at once. The bank, the reason and its wording, the amount, the currency and the dispute date reach the document with the next Update evidence.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={saving || !changed} startIcon={saving ? <CircularProgress size={16} /> : null}>Save</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
